import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiTarget } from 'react-icons/fi';
import { getAvailableQuestionCount } from '../../api/topicExercisesAPI';

const cardStyle = {
  padding: '1rem',
  borderRadius: '12px',
  border: '1px solid var(--liwaza-outline-variant)',
  background:
    'linear-gradient(180deg, var(--liwaza-surface-container-high), var(--liwaza-surface-container))',
};

const actionButtonStyle = {
  width: '100%',
  border: 'none',
  borderRadius: '10px',
  padding: '0.85rem 1rem',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '0.95rem',
};

const LessonTopicPracticeCard = ({
  topicId,
  topicIds,
  topicLabel,
  subconcepts = [],
}) => {
  const navigate = useNavigate();
  const resolvedTopicIds = useMemo(
    () =>
      (Array.isArray(topicIds) && topicIds.length
        ? topicIds
        : [topicId]
      ).filter(Boolean),
    [topicId, topicIds]
  );
  const [available, setAvailable] = useState(0);
  const [isLoadingCounts, setIsLoadingCounts] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadCounts = async () => {
      setIsLoadingCounts(true);
      try {
        const [medium, hard] = await Promise.all([
          getAvailableQuestionCount(resolvedTopicIds, 'medium', subconcepts),
          getAvailableQuestionCount(resolvedTopicIds, 'hard', subconcepts),
        ]);

        if (!isMounted) return;
        setAvailable((medium || 0) + (hard || 0));
      } catch (error) {
        console.error('Failed to load lesson practice counts:', error);
      } finally {
        if (isMounted) {
          setIsLoadingCounts(false);
        }
      }
    };

    loadCounts();

    return () => {
      isMounted = false;
    };
  }, [resolvedTopicIds, subconcepts]);

  const startMediumHardSession = () => {
    if (available <= 0) return;

    const sessionConfig = {
      id: `session_${Date.now()}`,
      topics: resolvedTopicIds,
      subconcepts,
      questions: Math.max(1, Math.min(20, available)),
      timing: 'untimed',
      timePerQuestion: 90,
      totalTime: 20,
      difficulty: 'mixed_sunday',
      difficultyMix: { medium: 0.5, hard: 0.5, very_hard: 0 },
      feedbackMode: 'deferred',
      questionPool: 'all',
      createdAt: new Date().toISOString(),
    };

    sessionStorage.setItem(
      'quantPracticeSession',
      JSON.stringify(sessionConfig)
    );
    navigate('/exams/gre/quant-practice/session');
  };

  const disabled = isLoadingCounts || available < 20;

  return (
    <div style={cardStyle}>
      <h4 style={{ margin: 0, fontSize: '1rem' }}>Medium/Hard questions</h4>
      <p
        style={{
          margin: '0.4rem 0 0',
          color: 'var(--liwaza-on-surface-variant)',
          fontSize: '0.92rem',
          lineHeight: 1.5,
        }}
      >
        20 medium/hard questions on {topicLabel}.
      </p>

      {!available && !isLoadingCounts ? (
        <p
          style={{
            margin: '0.9rem 0 0',
            color: 'var(--liwaza-on-surface-variant)',
            fontSize: '0.9rem',
          }}
        >
          No medium or hard questions are available yet for this lesson.
        </p>
      ) : (
        <button
          type="button"
          onClick={startMediumHardSession}
          disabled={disabled}
          style={{
            ...actionButtonStyle,
            marginTop: '1rem',
            background: disabled
              ? 'var(--liwaza-surface-container-highest)'
              : 'var(--liwaza-secondary-container)',
            color: disabled
              ? 'var(--liwaza-on-surface-variant)'
              : 'var(--liwaza-on-secondary-container)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.75rem',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <span
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <FiTarget size={16} />
            20 Medium/Hard
          </span>
          <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>
            {isLoadingCounts ? '...' : `${available} avail.`}
          </span>
        </button>
      )}
    </div>
  );
};

export default LessonTopicPracticeCard;
